import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { accessToken } from './agent';
import { creatorApiBaseURL } from './creatorApiConfig';
import type { IAiMediaConnection, IAiSessionConnection, ICreatorHubConnection, ICreatorHubConnectionHandlers } from './creatorHubConnectionContract';
import type { TSignalRProgressEnvelope } from '../models/AiSessionModels';
import type { TSignalRMediaGenerationEnvelope } from '../models/AiMediaModels';

/**
 * The skeleton every creator API hub connection is built from: lifecycle, connection id,
 * subscribe/unsubscribe and reconnection. The two hubs at the bottom of this file supply nothing
 * but the names the backend maps — the hub path, its one channel and its two group methods.
 *
 * Knows nothing about courses, contents, messages or React. It emits parsed envelopes and never
 * decides what one means — that is the consuming store's job, and this module must not import one.
 */

export interface ICreatorHubConnectionOptions {
  /** The hub's path under `hubs/`, as the backend maps it — `aiSession`, `aiMedia`. */
  hubPath: string;
  /** The one client method the hub invokes, carrying an envelope. */
  channel: string;
  subscribeMethod: string;
  unsubscribeMethod: string;
}

/**
 * A hub is typed on the backend, so the shape is known — this only rejects payloads that are not
 * envelopes at all, rather than re-validating a contract the compiler already mirrors.
 */
export const parseHubEnvelope = <TEnvelope>(raw: unknown): TEnvelope | null => {
  if (raw === null || typeof raw !== 'object') return null;
  if (typeof (raw as { success?: unknown }).success !== 'boolean') return null;

  return raw as TEnvelope;
};

export const createCreatorHubConnection = <TEnvelope, TSubscribeArgs extends unknown[] = []>(options: ICreatorHubConnectionOptions): ICreatorHubConnection<TEnvelope, TSubscribeArgs> => {
  let handlers: Partial<ICreatorHubConnectionHandlers<TEnvelope>> = {};
  let connection: HubConnection | null = null;
  let startPromise: Promise<string> | null = null;
  /**
   * The connection we asked to close, held so its `onclose` can be told from a socket that dropped.
   * Only the second of those is a lost connection, and only that one is reported.
   */
  let stoppingConnection: HubConnection | null = null;

  const buildConnection = (): HubConnection => {
    const hubConnection = new HubConnectionBuilder()
      .withUrl(`${creatorApiBaseURL}hubs/${options.hubPath}`, { accessTokenFactory: () => accessToken })
      .withAutomaticReconnect()
      .build();

    hubConnection.on(options.channel, (raw: unknown) => {
      const envelope = parseHubEnvelope<TEnvelope>(raw);
      if (envelope) handlers.onEnvelope?.(envelope);
    });

    hubConnection.onreconnecting(() => handlers.onReconnecting?.());

    // A group membership goes with the socket that held it, so a socket that came back on its own
    // is in no group until it asks again.
    hubConnection.onreconnected(() => handlers.onReconnected?.());

    hubConnection.onclose(() => {
      connection = null;
      startPromise = null;

      if (stoppingConnection === hubConnection) stoppingConnection = null;
      else handlers.onClosed?.();
    });

    return hubConnection;
  };

  const requireConnection = (): HubConnection => {
    if (!connection) throw new Error(`The ${options.hubPath} hub connection is not started.`);
    return connection;
  };

  return {
    get connectionId() {
      return connection?.connectionId ?? null;
    },

    setHandlers: (nextHandlers) => {
      handlers = { ...handlers, ...nextHandlers };
    },

    // Concurrent callers share one handshake: a send and a resume can both ask for the connection
    // id before either has completed, and an editor reopened while the first open is still in
    // flight must not start a second socket.
    start: () => {
      if (connection?.state === HubConnectionState.Connected && connection.connectionId) {
        return Promise.resolve(connection.connectionId);
      }

      if (!startPromise) {
        const hubConnection = connection ?? buildConnection();
        connection = hubConnection;

        startPromise = hubConnection
          .start()
          .then(() => {
            if (!hubConnection.connectionId) throw new Error(`The ${options.hubPath} hub connection started without a connection id.`);
            return hubConnection.connectionId;
          })
          .catch((error) => {
            connection = null;
            startPromise = null;
            throw error;
          });
      }

      return startPromise;
    },

    // Async so a connection that is not there rejects rather than throwing at the call site, where
    // the caller's `.catch` has not been attached yet.
    subscribe: async (...args: TSubscribeArgs) => {
      await requireConnection().invoke(options.subscribeMethod, ...args);
    },

    // A membership goes with the socket that held it, so a closed connection has nothing left to
    // give up — and giving one up after its socket has already gone is the ordinary case.
    unsubscribe: async (...args: TSubscribeArgs) => {
      if (!connection) return;

      await connection.invoke(options.unsubscribeMethod, ...args);
    },

    stop: async () => {
      const hubConnection = connection;
      connection = null;
      startPromise = null;

      if (hubConnection) {
        stoppingConnection = hubConnection;
        await hubConnection.stop().catch(() => undefined);
      }
    },
  };
};

/** The AI session hub as the backend names it: one progress channel, joined and left per session id. */
export const createAiSessionConnection = (): IAiSessionConnection =>
  createCreatorHubConnection<TSignalRProgressEnvelope, [aiSessionId: number]>({
    hubPath: 'aiSession',
    channel: 'OnAiProgressUpdate',
    subscribeMethod: 'SubscribeToSessionAsync',
    unsubscribeMethod: 'UnsubscribeFromSessionAsync',
  });

/**
 * The AI media hub as the backend names it: one terminal-event channel, joined owner-wide, so
 * subscribe takes no arguments. Nothing here hands the connection id on to a request: the
 * subscription is owner-wide and asked for by the client, so no media request has to name the
 * socket it wants its answer on.
 */
export const createAiMediaConnection = (): IAiMediaConnection =>
  createCreatorHubConnection<TSignalRMediaGenerationEnvelope>({
    hubPath: 'aiMedia',
    channel: 'OnAiMediaGenerationFinished',
    subscribeMethod: 'SubscribeToMediaAsync',
    unsubscribeMethod: 'UnsubscribeFromMediaAsync',
  });
